import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  Download,
  Loader2,
  Plus,
  Trash2,
  Pencil,
  ShieldCheck,
  Sparkles,
  AlertTriangle,
  Copy,
  QrCode,
  Upload,
  X as CloseIcon,
} from "lucide-react";
import { API_BASE, formatApiError } from "@/lib/api";
import { generateResumePDF } from "@/lib/resume";
import { toast } from "sonner";

const EMPTY_PREV = { company: "", designation: "", duration: "", role: "" };
const EMPTY_EDU = { school: "", board: "", year: "", percentage: "" };

export default function ResumeBuilderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const nav = useNavigate();
  const [cand, setCand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [edit, setEdit] = useState(false);
  const [agreedNoRefund, setAgreedNoRefund] = useState(false);
  const [upiOpen, setUpiOpen] = useState(false);
  const [upiCfg, setUpiCfg] = useState(null);
  const [utr, setUtr] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);

  // Editable form mirror
  const [form, setForm] = useState(null);

  const load = async () => {
    if (!token) {
      toast.error("Invalid link");
      nav("/");
      return;
    }
    try {
      const { data } = await axios.get(
        `${API_BASE}/candidates/public/${id}?token=${token}`
      );
      setCand(data);
      setForm({
        career_objective: data.career_objective || "",
        skills: (data.skills || []).join(", "),
        certifications: (data.certifications || []).join(", "),
        languages: (data.languages || []).join(", "),
        previous_companies:
          data.previous_companies && data.previous_companies.length
            ? data.previous_companies
            : [{ ...EMPTY_PREV }],
        edu_10th: data.edu_10th || { ...EMPTY_EDU },
        edu_12th: data.edu_12th || { ...EMPTY_EDU },
        edu_graduation: data.edu_graduation || { ...EMPTY_EDU },
        edu_post_graduation: data.edu_post_graduation || { ...EMPTY_EDU },
      });
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id, token]);

  const isComplete = useMemo(() => {
    if (!cand) return false;
    const minimum = [
      cand.career_objective || form?.career_objective,
      (cand.skills || []).length > 0 || form?.skills,
      cand.edu_graduation?.school || form?.edu_graduation?.school,
    ];
    return minimum.every(Boolean);
  }, [cand, form]);

  const payload = () => ({
    career_objective: form.career_objective || null,
    skills: form.skills
      .split(",").map((s) => s.trim()).filter(Boolean),
    certifications: form.certifications
      .split(",").map((s) => s.trim()).filter(Boolean),
    languages: form.languages
      .split(",").map((s) => s.trim()).filter(Boolean),
    previous_companies: form.previous_companies.filter(
      (c) => c.company || c.designation || c.duration || c.role
    ),
    edu_10th: cleanEdu(form.edu_10th),
    edu_12th: cleanEdu(form.edu_12th),
    edu_graduation: cleanEdu(form.edu_graduation),
    edu_post_graduation: cleanEdu(form.edu_post_graduation),
  });

  const saveOnly = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API_BASE}/candidates/public/${id}?token=${token}`,
        payload()
      );
      setCand(data);
      setEdit(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Save then upload PDF (free path)
  const saveAndSubmit = async () => {
    setSaving(true);
    try {
      const { data: updated } = await axios.patch(
        `${API_BASE}/candidates/public/${id}?token=${token}`,
        payload()
      );
      const { blob } = generateResumePDF(updated);
      const fd = new FormData();
      const file = new File(
        [blob],
        `${(updated.full_name || "resume").replace(/\s+/g, "_")}_resume.pdf`,
        { type: "application/pdf" }
      );
      fd.append("file", file);
      fd.append("purpose", "resume");
      const { data: up } = await axios.post(`${API_BASE}/upload`, fd);
      await axios.patch(
        `${API_BASE}/candidates/public/${id}?token=${token}`,
        { cv_path: up.path, has_cv: true }
      );
      toast.success("Submitted to recruiter");
      nav(`/submitted`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const downloadNow = async () => {
    // Save first; then generate + download locally (no upload required for paid download path)
    setSaving(true);
    try {
      const { data: updated } = await axios.patch(
        `${API_BASE}/candidates/public/${id}?token=${token}`,
        payload()
      );
      const { download } = generateResumePDF(updated);
      download(`${(updated.full_name || "candidate").replace(/\s+/g, "_")}_resume.pdf`);
      toast.success("Resume downloaded!");
      setCand(updated);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const payAndDownload = async () => {
    setPaying(true);
    try {
      // 1. Save latest edits first
      const { data: updated } = await axios.patch(
        `${API_BASE}/candidates/public/${id}?token=${token}`,
        payload()
      );
      setCand(updated);

      // 2. Already paid? Skip payment modal, download immediately
      if (updated.resume_download_paid) {
        const { download } = generateResumePDF(updated);
        download(
          `${(updated.full_name || "candidate").replace(/\s+/g, "_")}_resume.pdf`
        );
        toast.success("Downloaded (already paid)");
        setPaying(false);
        return;
      }

      // 3. Fetch UPI config once and open modal
      if (!upiCfg) {
        const { data } = await axios.get(`${API_BASE}/payments/upi-config`);
        setUpiCfg(data);
      }
      setUpiOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  const submitUpiPayment = async () => {
    const cleaned = (utr || "").trim();
    if (cleaned.length < 6) {
      toast.error("Please enter a valid UTR / transaction reference number");
      return;
    }
    if (!screenshotFile) {
      toast.error("Please upload the payment screenshot");
      return;
    }
    setPaying(true);
    try {
      // 1. Upload screenshot
      const fd = new FormData();
      fd.append("file", screenshotFile);
      fd.append("purpose", "payment_screenshot");
      const { data: up } = await axios.post(`${API_BASE}/upload`, fd);

      // 2. Submit UTR + screenshot path
      await axios.post(`${API_BASE}/payments/upi-submit`, {
        candidate_id: id,
        edit_token: token,
        utr: cleaned,
        screenshot_path: up.path,
      });

      // 3. Mark local state paid & download PDF
      const paidCand = { ...cand, resume_download_paid: true };
      setCand(paidCand);
      const { download } = generateResumePDF(paidCand);
      download(
        `${(paidCand.full_name || "candidate").replace(/\s+/g, "_")}_resume.pdf`
      );
      toast.success("Payment recorded — resume downloaded!");
      setUpiOpen(false);
      setUtr("");
      setScreenshotFile(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  if (!cand || !form) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-ink-600">
        Loading resume…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfdfc]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size={42} />
          <Badge className="bg-brand-50 text-brand-800 border-0">
            <Sparkles size={12} className="mr-1" /> Resume Builder
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        <div className="mb-6 animate-fade-in-up">
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-ink-900">
            Hi {cand.full_name?.split(" ")[0]}, complete your resume
          </h1>
          <p className="text-sm text-ink-600 mt-1">
            Add your previous experience, full education and certifications. You can
            either submit it free to our recruiters, or pay ₹200 to download the PDF.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Editor */}
          <Card className="tj-card">
            <CardContent className="p-5 md:p-6 space-y-5">
              <SectionHead
                title="Career Objective"
                hint="2-3 lines about what you are looking for"
              />
              <Textarea
                data-testid="rb-career-objective"
                rows={3}
                value={form.career_objective}
                onChange={(e) => setForm({ ...form, career_objective: e.target.value })}
              />

              <SectionHead title="Education (year-wise)" />
              <EduRow
                label="10th"
                value={form.edu_10th}
                onChange={(v) => setForm({ ...form, edu_10th: v })}
                testid="rb-edu-10"
              />
              <EduRow
                label="12th"
                value={form.edu_12th}
                onChange={(v) => setForm({ ...form, edu_12th: v })}
                testid="rb-edu-12"
              />
              <EduRow
                label="Graduation"
                value={form.edu_graduation}
                onChange={(v) => setForm({ ...form, edu_graduation: v })}
                testid="rb-edu-grad"
              />
              <EduRow
                label="Post-Graduation (optional)"
                value={form.edu_post_graduation}
                onChange={(v) => setForm({ ...form, edu_post_graduation: v })}
                testid="rb-edu-pg"
              />

              <SectionHead
                title="Previous Companies"
                hint="Add at least your most recent two employments"
              />
              {form.previous_companies.map((c, i) => (
                <div
                  key={i}
                  data-testid={`rb-prev-${i}`}
                  className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50/50"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-brand-700 uppercase tracking-wider">
                      #{i + 1}
                    </span>
                    {form.previous_companies.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const next = form.previous_companies.filter(
                            (_, idx) => idx !== i
                          );
                          setForm({ ...form, previous_companies: next });
                        }}
                        className="text-xs text-rose-700 inline-flex items-center gap-1 hover:underline"
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <FieldSmall label="Company Name">
                      <Input
                        value={c.company}
                        onChange={(e) =>
                          updatePrev(form, setForm, i, "company", e.target.value)
                        }
                      />
                    </FieldSmall>
                    <FieldSmall label="Designation">
                      <Input
                        value={c.designation}
                        onChange={(e) =>
                          updatePrev(form, setForm, i, "designation", e.target.value)
                        }
                      />
                    </FieldSmall>
                    <FieldSmall label="Duration (e.g. Jan 2022 – Mar 2024)">
                      <Input
                        value={c.duration}
                        onChange={(e) =>
                          updatePrev(form, setForm, i, "duration", e.target.value)
                        }
                      />
                    </FieldSmall>
                    <FieldSmall label="Role / Responsibilities" full>
                      <Textarea
                        rows={2}
                        value={c.role}
                        onChange={(e) =>
                          updatePrev(form, setForm, i, "role", e.target.value)
                        }
                      />
                    </FieldSmall>
                  </div>
                </div>
              ))}
              <Button
                data-testid="add-prev-btn"
                variant="outline"
                onClick={() =>
                  setForm({
                    ...form,
                    previous_companies: [
                      ...form.previous_companies,
                      { ...EMPTY_PREV },
                    ],
                  })
                }
                className="border-brand-200 text-brand-700 hover:bg-brand-50"
              >
                <Plus size={14} className="mr-1" /> Add another company
              </Button>

              <SectionHead title="Skills (comma separated)" />
              <Input
                data-testid="rb-skills"
                value={form.skills}
                onChange={(e) => setForm({ ...form, skills: e.target.value })}
                placeholder="e.g. Excel, Tally, Customer Service"
              />

              <SectionHead title="Certifications (comma separated)" />
              <Input
                data-testid="rb-certifications"
                value={form.certifications}
                onChange={(e) => setForm({ ...form, certifications: e.target.value })}
                placeholder="e.g. PMP, AWS Cloud Practitioner"
              />

              <SectionHead title="Languages" />
              <Input
                data-testid="rb-languages"
                value={form.languages}
                onChange={(e) => setForm({ ...form, languages: e.target.value })}
                placeholder="e.g. English, Hindi, Marathi"
              />

              <Button
                data-testid="save-only-btn"
                onClick={saveOnly}
                disabled={saving}
                variant="outline"
                className="w-full border-brand-200 text-brand-700 hover:bg-brand-50"
              >
                {saving ? (
                  <Loader2 size={14} className="mr-1 animate-spin" />
                ) : (
                  <Pencil size={14} className="mr-1" />
                )}
                Save changes
              </Button>
            </CardContent>
          </Card>

          {/* Preview + actions */}
          <div className="space-y-6">
            <Card className="tj-card lg:sticky lg:top-6">
              <CardContent className="p-5 md:p-6">
                <h3 className="font-heading text-base font-semibold text-ink-900 mb-3">
                  Preview
                </h3>
                <ResumePreview cand={cand} form={form} />
              </CardContent>
            </Card>

            <Card className="tj-card">
              <CardContent className="p-5 md:p-6 space-y-4">
                <h3 className="font-heading text-base font-semibold text-ink-900">
                  Choose how to finish
                </h3>

                {/* Free option */}
                <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 size={16} className="text-brand-700" />
                    <span className="font-medium text-ink-900">
                      Submit to Tulsi Jobs (Free)
                    </span>
                  </div>
                  <p className="text-xs text-ink-700 mb-3">
                    Your resume is submitted to our recruiters. We&apos;ll reach out
                    when there&apos;s a matching role. No download.
                  </p>
                  <Button
                    data-testid="submit-free-btn"
                    onClick={saveAndSubmit}
                    disabled={saving || paying}
                    className="w-full bg-brand-700 hover:bg-brand-800 text-white"
                  >
                    {saving ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : null}
                    Submit free to recruiter
                  </Button>
                </div>

                {/* Paid option */}
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Download size={16} className="text-amber-700" />
                    <span className="font-medium text-ink-900">
                      Download PDF — ₹200
                    </span>
                    {cand.resume_download_paid && (
                      <Badge className="ml-auto bg-emerald-100 text-emerald-800 border-0">
                        Paid
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-ink-700 mb-3">
                    Pay ₹200 via UPI (Google Pay / PhonePe / Paytm / any UPI app) and
                    download your resume PDF. You also stay in our talent pool.
                  </p>
                  <Button
                    data-testid="pay-download-btn"
                    onClick={cand.resume_download_paid ? downloadNow : payAndDownload}
                    disabled={paying || saving || !agreedNoRefund}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
                  >
                    {paying ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" /> Please
                        wait…
                      </>
                    ) : cand.resume_download_paid ? (
                      <>
                        <Download size={14} className="mr-2" /> Download PDF
                      </>
                    ) : (
                      <>
                        Pay ₹200 via UPI &amp; Download{" "}
                        <QrCode size={14} className="ml-2" />
                      </>
                    )}
                  </Button>

                  {/* Non-refund notice */}
                  {!cand.resume_download_paid && (
                    <div
                      data-testid="no-refund-notice"
                      className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3"
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle
                          size={14}
                          className="text-rose-700 mt-0.5 shrink-0"
                        />
                        <div className="text-[11px] leading-snug text-rose-900">
                          <strong className="block mb-0.5">
                            No Refund Policy
                          </strong>
                          All payments are <strong>final and non-refundable</strong> under
                          any circumstances. Once the ₹200 fee is paid, the resume PDF is
                          delivered immediately and the transaction cannot be reversed.
                          Please review your details before proceeding.
                        </div>
                      </div>
                      <label className="mt-2 flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="agree-no-refund-checkbox"
                          checked={agreedNoRefund}
                          onChange={(e) => setAgreedNoRefund(e.target.checked)}
                          className="mt-0.5 h-3.5 w-3.5 rounded border-rose-300 text-rose-700 focus:ring-rose-500"
                        />
                        <span className="text-[11px] text-rose-900">
                          I understand and accept that my payment of ₹200 is{" "}
                          <strong>non-refundable</strong>. (
                          <a
                            href="/refund-policy"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="refund-policy-link"
                            className="underline hover:no-underline"
                          >
                            read full policy
                          </a>
                          )
                        </span>
                      </label>
                    </div>
                  )}
                  <div className="mt-2 text-[11px] text-ink-600 inline-flex items-center gap-1">
                    <ShieldCheck size={11} /> Direct UPI to M/S TULSI JOBS · Manual
                    verification
                  </div>
                </div>

                {!isComplete && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    Tip: Fill the career objective, your graduation details and at
                    least one previous company for a stronger resume.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      {upiOpen && upiCfg && (
        <UpiPaymentModal
          cfg={upiCfg}
          candidateName={cand.full_name}
          utr={utr}
          setUtr={setUtr}
          screenshotFile={screenshotFile}
          setScreenshotFile={setScreenshotFile}
          onClose={() => {
            setUpiOpen(false);
            setUtr("");
            setScreenshotFile(null);
          }}
          onSubmit={submitUpiPayment}
          submitting={paying}
        />
      )}
      <footer className="text-center text-xs text-ink-600 py-8">
        © {new Date().getFullYear()} Tulsi Jobs Placement Consultant
      </footer>
    </div>
  );
}

// ----------- helpers ------------
function cleanEdu(e) {
  if (!e) return null;
  const any = e.school || e.board || e.year || e.percentage;
  return any ? e : null;
}

function updatePrev(form, setForm, idx, field, val) {
  const next = form.previous_companies.map((c, i) =>
    i === idx ? { ...c, [field]: val } : c
  );
  setForm({ ...form, previous_companies: next });
}

function SectionHead({ title, hint }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-[0.16em] font-semibold text-brand-700">
        {title}
      </h4>
      {hint && <p className="text-[11px] text-ink-600 mt-0.5">{hint}</p>}
    </div>
  );
}

function FieldSmall({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-[11px] text-ink-600">{label}</Label>
      {children}
    </div>
  );
}

function EduRow({ label, value, onChange, testid }) {
  const v = value || { ...EMPTY_EDU };
  return (
    <div data-testid={testid} className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
      <div className="col-span-2 sm:col-span-1">
        <Label className="text-[11px] text-ink-600">{label}</Label>
        <Input
          value={v.school}
          onChange={(e) => onChange({ ...v, school: e.target.value })}
          placeholder="School / College"
        />
      </div>
      <div>
        <Label className="text-[11px] text-ink-600">Board / University</Label>
        <Input
          value={v.board}
          onChange={(e) => onChange({ ...v, board: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-[11px] text-ink-600">Year</Label>
        <Input
          value={v.year}
          onChange={(e) => onChange({ ...v, year: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-[11px] text-ink-600">% / CGPA</Label>
        <Input
          value={v.percentage}
          onChange={(e) => onChange({ ...v, percentage: e.target.value })}
        />
      </div>
    </div>
  );
}

function ResumePreview({ cand, form }) {
  const skills = form.skills.split(",").map((s) => s.trim()).filter(Boolean);
  const langs = form.languages.split(",").map((s) => s.trim()).filter(Boolean);
  const certs = form.certifications.split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      <div className="bg-brand-700 text-white px-5 py-4">
        <div className="text-lg font-heading font-bold">{cand.full_name}</div>
        <div className="text-xs opacity-90">{cand.job_category}</div>
        <div className="text-[11px] opacity-80 mt-1">
          {[cand.phone, cand.email, cand.current_location].filter(Boolean).join("  •  ")}
        </div>
      </div>
      <div className="p-5 space-y-4 text-sm">
        {form.career_objective && (
          <PreviewSection title="Objective">
            <p className="text-ink-700">{form.career_objective}</p>
          </PreviewSection>
        )}
        <PreviewSection title="Education">
          {["edu_10th", "edu_12th", "edu_graduation", "edu_post_graduation"]
            .map((k) => ({ k, v: form[k] }))
            .filter(({ v }) => v && (v.school || v.board || v.year))
            .map(({ k, v }) => (
              <div key={k} className="text-xs text-ink-700">
                <span className="font-medium text-ink-900">
                  {k.replace("edu_", "").replace("_", " ")}:
                </span>{" "}
                {v.school} {v.board && `(${v.board})`} {v.year && `· ${v.year}`}{" "}
                {v.percentage && `· ${v.percentage}`}
              </div>
            ))}
        </PreviewSection>
        <PreviewSection title="Experience">
          {form.previous_companies
            .filter((c) => c.company || c.designation)
            .map((c, i) => (
              <div key={i} className="text-xs text-ink-700">
                <div className="font-medium text-ink-900">
                  {c.company} {c.designation && `· ${c.designation}`}
                </div>
                <div className="text-ink-600">{c.duration}</div>
                <div>{c.role}</div>
              </div>
            ))}
        </PreviewSection>
        {skills.length > 0 && (
          <PreviewSection title="Skills">
            <div className="flex flex-wrap gap-1.5">
              {skills.map((s) => (
                <span key={s} className="tj-pill bg-brand-50 text-brand-800">
                  {s}
                </span>
              ))}
            </div>
          </PreviewSection>
        )}
        {certs.length > 0 && (
          <PreviewSection title="Certifications">
            <p className="text-xs text-ink-700">{certs.join(" • ")}</p>
          </PreviewSection>
        )}
        {langs.length > 0 && (
          <PreviewSection title="Languages">
            <p className="text-xs text-ink-700">{langs.join(" • ")}</p>
          </PreviewSection>
        )}
      </div>
    </div>
  );
}

function PreviewSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-brand-700 mb-1">
        {title}
      </div>
      {children}
    </div>
  );
}

function UpiPaymentModal({
  cfg,
  candidateName,
  utr,
  setUtr,
  screenshotFile,
  setScreenshotFile,
  onClose,
  onSubmit,
  submitting,
}) {
  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(cfg.upi_id);
      toast.success("UPI ID copied");
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };
  return (
    <div
      data-testid="upi-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8 overflow-hidden animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-brand-700 to-brand-800 text-white">
          <div>
            <h3 className="font-heading text-lg font-semibold">
              Pay ₹{cfg.amount} via UPI
            </h3>
            <p className="text-[11px] opacity-90 mt-0.5">
              {candidateName ? `For ${candidateName} · ` : ""}Resume PDF Download
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="upi-modal-close"
            className="h-8 w-8 rounded-lg hover:bg-white/10 grid place-items-center"
            aria-label="Close"
          >
            <CloseIcon size={18} />
          </button>
        </div>

        <div className="p-5 md:p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          <ol className="text-sm text-ink-700 space-y-1 list-decimal pl-5">
            <li>Open any UPI app (GPay, PhonePe, Paytm, BHIM, or your bank app)</li>
            <li>Scan the QR below <span className="text-ink-500">OR</span> pay to the UPI ID</li>
            <li>Enter amount <strong>₹{cfg.amount}</strong> and complete payment</li>
            <li>Enter the UTR / Transaction ID + upload screenshot below</li>
          </ol>

          <div className="grid sm:grid-cols-2 gap-4 items-center">
            <div className="rounded-xl border border-slate-200 p-3 bg-white grid place-items-center">
              <img
                src={cfg.qr_image}
                alt="UPI QR — M/S Tulsi Jobs"
                data-testid="upi-qr-image"
                className="w-full max-w-[220px] h-auto"
              />
              <div className="mt-2 text-[11px] text-ink-600 text-center">
                {cfg.merchant_name}
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.14em] text-ink-600">
                  UPI ID
                </Label>
                <div className="mt-1 flex items-center gap-2">
                  <code
                    data-testid="upi-id-text"
                    className="flex-1 text-sm font-mono bg-slate-100 px-3 py-2 rounded-md border border-slate-200 truncate"
                  >
                    {cfg.upi_id}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={copyUpi}
                    data-testid="upi-copy-btn"
                    className="border-brand-200 text-brand-700 hover:bg-brand-50 shrink-0"
                  >
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.14em] text-ink-600">
                  Amount
                </Label>
                <div className="mt-1 text-2xl font-heading font-bold text-brand-800">
                  ₹{cfg.amount}
                </div>
              </div>
              <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2">
                Do not close this window before entering the transaction reference
                and screenshot below.
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-3">
            <div>
              <Label
                htmlFor="utr-input"
                className="text-xs uppercase tracking-[0.14em] text-ink-600 font-medium"
              >
                UTR / Transaction Reference Number *
              </Label>
              <Input
                id="utr-input"
                data-testid="upi-utr-input"
                value={utr}
                onChange={(e) => setUtr(e.target.value.trim())}
                placeholder="e.g. 429917123456"
                className="mt-1"
                maxLength={30}
              />
              <p className="text-[11px] text-ink-500 mt-1">
                12-digit reference number shown in your UPI app after payment.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.14em] text-ink-600 font-medium">
                Payment Screenshot *
              </Label>
              <label
                data-testid="upi-screenshot-drop"
                className="mt-1 flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-brand-400 hover:bg-brand-50/40 transition-colors"
              >
                <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 grid place-items-center">
                  <Upload size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink-900 truncate">
                    {screenshotFile
                      ? screenshotFile.name
                      : "Tap to upload payment screenshot"}
                  </div>
                  <div className="text-[11px] text-ink-600">
                    {screenshotFile
                      ? `${(screenshotFile.size / 1024).toFixed(0)} KB`
                      : "JPG / PNG · Max 10 MB"}
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="upi-screenshot-input"
                  onChange={(e) =>
                    setScreenshotFile(e.target.files?.[0] || null)
                  }
                />
              </label>
            </div>
          </div>

          <Button
            data-testid="upi-submit-btn"
            disabled={submitting || !utr || !screenshotFile}
            onClick={onSubmit}
            className="w-full bg-brand-700 hover:bg-brand-800 text-white disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" /> Submitting…
              </>
            ) : (
              <>
                Confirm Payment &amp; Download Resume{" "}
                <Download size={14} className="ml-2" />
              </>
            )}
          </Button>

          <p className="text-[11px] text-ink-500 text-center">
            Our team will verify your payment. Any false UTR may lead to account
            block and legal action.
          </p>
        </div>
      </div>
    </div>
  );
}
